import { useEffect } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { cn, openGoogleMaps } from "../../lib/utils";

// Create custom marker icon with project accent color (#cdbf81)
const createCustomIcon = () => {
  return L.divIcon({
    className: "custom-marker",
    html: `
      <div style="
        width: 30px;
        height: 30px;
        background-color: var(--color-accent);
        border: 3px solid var(--color-accent-glow);
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        cursor: pointer;
      ">
        <div style="
          width: 12px;
          height: 12px;
          background-color: var(--color-accent-glow);
          border-radius: 50%;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(45deg);
        "></div>
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
  });
};

// Component to update map view when coordinates change
function MapUpdater({
  latitude,
  longitude,
  zoom,
}: {
  latitude: number;
  longitude: number;
  zoom: number;
}) {
  const map = useMap();

  useEffect(() => {
    map.setView([latitude, longitude], zoom);
    // Disable all interactions
    map.dragging.disable();
    map.touchZoom.disable();
    map.doubleClickZoom.disable();
    map.scrollWheelZoom.disable();
    map.boxZoom.disable();
    map.keyboard.disable();
  }, [map, latitude, longitude, zoom]);

  return null;
}

interface MapProps {
  latitude: number;
  longitude: number;
  className?: string;
  height: string;
  width: string;
  zoom?: number;
}

export default function Map({
  latitude,
  longitude,
  className,
  height,
  width,
  zoom = 13,
}: MapProps) {
  const customIcon = createCustomIcon();

  const handleMarkerClick = () => {
    openGoogleMaps(latitude, longitude);
  };

  return (
    <div
      className={cn("w-full rounded-md overflow-hidden", className)}
      style={{ height, width }}
    >
      <style>{`
        .leaflet-container {
          background-color: var(--color-card);
        }
        .leaflet-control-container {
          display: none !important;
        }
        .leaflet-tile-container img {
          filter: brightness(0.2) contrast(1) saturate(0);
        }
        .custom-marker {
          cursor: pointer;
        }
      `}</style>
      <MapContainer
        center={[latitude, longitude]}
        zoom={zoom}
        scrollWheelZoom={false}
        dragging={false}
        touchZoom={false}
        doubleClickZoom={false}
        boxZoom={false}
        zoomControl={false}
        attributionControl={false}
        keyboard={false}
        style={{ height: "100%", width: "100%", zIndex: 0 }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution=""
        />
        <MapUpdater latitude={latitude} longitude={longitude} zoom={zoom} />
        <Marker
          position={[latitude, longitude]}
          icon={customIcon}
          eventHandlers={{
            click: handleMarkerClick,
          }}
        />
      </MapContainer>
    </div>
  );
}
